import { createTheme as createMuiTheme } from '@material-ui/core/styles';
import type { CSSProperties } from '@material-ui/core/styles/withStyles';

import { generateColorFilter, getTextColor } from '@/utils/colorHelpers';

declare module '@material-ui/core/styles/createPalette' {
    interface CommonColors {
        darkGrey: string;
        midGrey: string;
        inputDisabledGray: string;
        brown: string;
        lightBrown: string;
        yellow: string;
        hoverYellow: string;
        green: string;
        cream: string;
        red: string;
        mediumBrown: string;
        footerBG: string;
        secondary: string;
        lightGray: string;
        creamCalendar: string;
        cream_disable: string;
        footerGreen: string;
        lightBrownDropdown: string;
        lightBrownSelected: string;
    }
}

declare module '@material-ui/core/styles/createTypography' {
    interface Typography {
        menu: CSSProperties;
        mainMenu: CSSProperties;
        HL1: CSSProperties;
        HL2: CSSProperties;
        HL3: CSSProperties;
        HL4: CSSProperties;
        HL5: CSSProperties;
        subheading: CSSProperties;
        BGHL1: CSSProperties;
        content: CSSProperties;
        contentMainText: CSSProperties;
        buttonText: CSSProperties;
        buttonTextNoColor: CSSProperties;
        errorText: CSSProperties;
    }
    interface TypographyOptions {
        menu?: CSSProperties;
        mainMenu?: CSSProperties;
        HL1?: CSSProperties;
        HL2?: CSSProperties;
        HL3?: CSSProperties;
        HL4?: CSSProperties;
        HL5?: CSSProperties;
        subheading?: CSSProperties;
        BGHL1?: CSSProperties;
        content?: CSSProperties;
        contentMainText?: CSSProperties;
        buttonText?: CSSProperties;
        buttonTextNoColor?: CSSProperties;
        errorText?: CSSProperties;
    }
}

declare module '@material-ui/core/styles/createTheme' {
    interface Theme {
        icons: {
            primary: { filter: string };
            secondary: { filter: string };
        };
        layout: { contentWidth: number };
        border: { light: string };
        radius: { default: string; field: string };
        button: Record<string, unknown>;
        buttonShape: { height: string; radius: string };
        weight: { body: number; subhead: number; heading: number };
    }
    interface ThemeOptions {
        icons?: {
            primary?: { filter: string };
            secondary?: { filter: string };
        };
        layout?: { contentWidth: number };
        border?: { light: string };
        radius?: { default: string; field: string };
        button?: Record<string, unknown>;
        buttonShape?: { height: string; radius: string };
        weight?: { body: number; subhead: number; heading: number };
    }
}

const FONT_FAMILY = "'Uncut Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const WEIGHT_BODY = 350;
const WEIGHT_SUBHEAD = 400;
const WEIGHT_HEADING = 500;
const TRACKING_HEADING = '-0.04em';
// eterno.health's 4px corners and 16px label, at Mantine's md height (42px) as used by eh-office-app.
const BUTTON_HEIGHT = '2.625rem';
// Default corner rounding used across the app (buttons, cards, chips, …).
const DEFAULT_RADIUS = '0.25rem';
const FIELD_RADIUS = '0.5rem';
const BUTTON_RADIUS = DEFAULT_RADIUS;
const BUTTON_FONT_SIZE = '1rem';

// Uncut Sans has a non-standard ital axis (0-11, not 0-1), so font-style alone only synthesises an oblique.
export const ITALIC = {
    fontStyle: 'italic',
    fontVariationSettings: "'ital' 11",
    WebkitFontSynthesis: 'none',
    fontSynthesis: 'none',
} as const;

const darkGrey = '#2e2e2e';
const midGrey = '#939090';
const lightGrey = '#e0e0e0';
const lightBrown = '#efede8';
const brown = '#ABABAB';
const yellow = '#ffc877';
const hvrYellow = '#E5B46B';

// Flat UI: a single light-grey border used instead of drop shadows across the app.
export const LIGHT_BORDER = `1px solid ${lightGrey}`;

export const SELECT_MENU_PROPS = {
    getContentAnchorEl: null,
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
} as const;

export const createTheme = (primaryColor = null, secondaryColor = null) => {
    return createMuiTheme({
        palette: {
            common: {
                darkGrey: `${darkGrey}`,
                midGrey: `${midGrey}`,
                inputDisabledGray: `${lightGrey}`,
                brown: `${brown}`,
                lightBrown: `${lightBrown}`,
                yellow: primaryColor ?? `${yellow}`,
                hoverYellow: primaryColor ?? `${hvrYellow}`,
                green: '#5ba668',
                cream: '#faf9f8',
                white: '#ffffff',
                red: '#da3030',
                mediumBrown: '#cac5b4',
                footerBG: '#E1DFD5',
                secondary: secondaryColor ?? '#4C726D',
                lightGray: '#FBFBF9',
                creamCalendar: '#fbfbf9',
                cream_disable: '#F9F8F4',
                footerGreen: secondaryColor ?? '#4C726D',
                lightBrownDropdown: '#F5F5F5',
                lightBrownSelected: '#DFDFDF',
            },
            primary: {
                main: '#faf9f8',
            },
            secondary: {
                main: `${lightBrown}`,
            },
        },
        // custom breakpoints
        breakpoints: {
            values: {
                xs: 0,
                sm: 768,
                md: 1024,
                lg: 1280,
                xl: 1920,
            },
        },
        icons: {
            primary: {
                filter: generateColorFilter(primaryColor ?? yellow),
            },
            secondary: {
                filter: generateColorFilter(secondaryColor ?? '#4C726D'),
            },
        },
        typography: {
            // One family, three weights — 350 body, 400 sub-header, 500 heading.
            fontFamily: FONT_FAMILY,
            fontWeightLight: WEIGHT_BODY,
            fontWeightRegular: WEIGHT_BODY,
            fontWeightMedium: WEIGHT_HEADING,
            menu: {
                textTransform: 'none',
                fontWeight: WEIGHT_HEADING,
                fontSize: '1rem',
            },
            mainMenu: {
                textTransform: 'none',
                fontWeight: WEIGHT_HEADING,
                fontSize: '1.125rem',
            },
            HL1: {
                fontWeight: WEIGHT_HEADING,
                fontSize: '1.5rem',
                textTransform: 'none',
                lineHeight: 1.125,
                letterSpacing: TRACKING_HEADING,
                color: `${darkGrey}`,
            },
            HL2: {
                fontWeight: WEIGHT_SUBHEAD,
                fontSize: '1.5rem',
                textTransform: 'none',
                lineHeight: 1.125,
                letterSpacing: TRACKING_HEADING,
                color: `${darkGrey}`,
            },
            HL3: {
                fontWeight: WEIGHT_HEADING,
                fontSize: '1.313rem',
                textTransform: 'none',
                lineHeight: 1.125,
                letterSpacing: TRACKING_HEADING,
                color: `${darkGrey}`,
            },
            HL4: {
                fontWeight: WEIGHT_BODY,
                fontSize: '0.75rem',
                textTransform: 'none',
                lineHeight: 1.5,
                color: `${brown}`,
            },
            HL5: {
                fontWeight: WEIGHT_SUBHEAD,
                fontSize: '1.313rem',
                textTransform: 'none',
                lineHeight: 1.5,
                color: `${darkGrey}`,
            },
            subheading: {
                fontWeight: WEIGHT_HEADING,
                fontSize: '1rem',
                textTransform: 'none',
                lineHeight: 1.5,
                color: `${darkGrey}`,
            },
            BGHL1: {
                fontWeight: WEIGHT_HEADING,
                fontSize: '1.75rem',
                textTransform: 'none',
                lineHeight: 1.125,
                letterSpacing: TRACKING_HEADING,
                color: `${darkGrey}`,
            },

            content: {
                fontWeight: WEIGHT_BODY,
                fontSize: '1rem',
                textTransform: 'none',
                lineHeight: 1.5,
                color: `${darkGrey}`,
            },
            contentMainText: {
                fontWeight: WEIGHT_BODY,
                fontSize: '1.125rem',
                textTransform: 'none',
                lineHeight: 1.5,
                color: `${darkGrey}`,
            },
            buttonText: {
                fontWeight: WEIGHT_HEADING,
                fontSize: BUTTON_FONT_SIZE,
                textTransform: 'none',
                lineHeight: 1.5,
                color: getTextColor(primaryColor ?? yellow, darkGrey, 'white'),
                paddingLeft: 36,
                paddingRight: 36,
            },
            buttonTextNoColor: {
                fontWeight: WEIGHT_HEADING,
                fontSize: BUTTON_FONT_SIZE,
                textTransform: 'none',
                lineHeight: 1.5,
                paddingLeft: 36,
                paddingRight: 36,
            },
            errorText: {
                fontWeight: WEIGHT_BODY,
                fontSize: '0.875rem',
                textTransform: 'none',
                lineHeight: 1.5,
                color: '#da3030',
            },
        },
        layout: {
            contentWidth: 1280,
        },
        border: {
            light: LIGHT_BORDER,
        },
        radius: {
            default: DEFAULT_RADIUS,
            field: FIELD_RADIUS,
        },
        buttonShape: {
            height: BUTTON_HEIGHT,
            radius: BUTTON_RADIUS,
        },
        weight: {
            body: WEIGHT_BODY,
            subhead: WEIGHT_SUBHEAD,
            heading: WEIGHT_HEADING,
        },
        button: {
            height: BUTTON_HEIGHT,
            backgroundColor: primaryColor ?? yellow,
            marginTop: 0,
            borderRadius: BUTTON_RADIUS,
            '&:hover': {
                backgroundColor: `${primaryColor ?? hvrYellow} !important`,
            },
        },

        overrides: {
            // MUI's own .MuiButton-root forces uppercase, which per-class overrides lose to.
            MuiButton: {
                root: {
                    textTransform: 'none',
                    borderRadius: BUTTON_RADIUS,
                    minHeight: BUTTON_HEIGHT,
                    fontSize: BUTTON_FONT_SIZE,
                },
            },
            MuiFilledInput: {
                root: {
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    paddingRight: 10,
                    borderRadius: 5,
                    fontSize: '1rem',
                    height: '100%',
                    '&:hover': {
                        backgroundColor: '#F9F8F4',
                    },
                    '&$focused': {
                        backgroundColor: 'white',
                        border: `2px solid ${primaryColor ?? yellow}`,
                    },
                },
            },
            MuiFormLabel: {
                root: {
                    overflow: 'hidden',
                    backgroundColor: 'transparent',
                    paddingRight: 10,
                    fontSize: '1rem',
                    color: `${darkGrey}`,

                    '&$focused': {
                        color: `${darkGrey}`,
                    },
                },
            },
            MuiInputLabel: {
                filled: {
                    '&$shrink': {
                        transform: 'translate(12px, 10px) scale(0.875)',
                    },
                },
            },
        },
        props: {
            MuiFilledInput: {
                disableUnderline: true,
            },
            MuiSelect: {
                MenuProps: SELECT_MENU_PROPS,
            },
        },
    });
};
